// Story 11.1 — Wrapper sobre `@monaco-editor/react`. Lazy-loaded.
// Tema oscuro forzado consistente con el resto de la UI; markers via
// `setModelMarkers` aplicados desde props. Cmd+S interceptado.
// Story 11.3 — extiende con `onCursorChange` y handle imperativo
// `revealLine` via `forwardRef` para sync con el FlowDiagram.

import Editor, { type OnMount } from '@monaco-editor/react';
import type { editor as MonacoEditor } from 'monaco-editor';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { ValidationProblem } from '@/lib/editor-types';
import { registerCompletionProvider } from './completion-provider';
import { setupMonaco } from './MonacoSetup';

// Singleton — el completion provider se registra una sola vez por proceso.
// Sin esto, abrir múltiples archivos genera duplicados en cada dropdown.
let completionProviderRegistered = false;

interface Props {
  path: string;
  language: 'yaml' | 'typescript' | 'plaintext';
  value: string;
  /** Si true, el editor es readonly. */
  readOnly?: boolean;
  onChange?: (next: string) => void;
  onSave?: () => void;
  /** Errores semánticos del DSL — se pintan via `setModelMarkers`. */
  problems?: ValidationProblem[];
  /** Warnings semánticos — pintados con severity Warning (subrayado verde) y tooltip. */
  warnings?: ValidationProblem[];
  /** Story 11.3 — reporta cambios de cursor (1-based lineNumber). */
  onCursorChange?: (line: number) => void;
  /** P-407 (review 11.3): el editor terminó de montar; el caller puede
   *  flushear cualquier acción imperativa que estaba esperando (e.g.
   *  un `revealLine` queued en cross-tab click). */
  onReady?: () => void;
}

/** Story 11.3 — handle imperativo para que `EditorPage` sincronice con el diagrama. */
export interface CodeEditorHandle {
  /** Scrollea (si fuera de viewport) y resalta la línea por 1.5s. */
  revealLine(line: number): void;
}

const THEME_NAME = 'revel-dark';
const HIGHLIGHT_DURATION_MS = 1500;

export const CodeEditor = forwardRef<CodeEditorHandle, Props>(function CodeEditor(
  {
    path,
    language,
    value,
    readOnly = false,
    onChange,
    onSave,
    problems,
    warnings,
    onCursorChange,
    onReady,
  },
  ref,
): React.ReactElement {
  const editorRef = useRef<MonacoEditor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof import('monaco-editor') | null>(null);
  const decorationsRef = useRef<MonacoEditor.IEditorDecorationsCollection | null>(null);
  const decorationTimerRef = useRef<number | null>(null);
  // P-404 (review 11.3): el listener de Monaco se registra una vez en
  // `handleMount`; capturaba la PRIMERA `onCursorChange` por closure y
  // jamás veía updates de la prop. Una ref siempre fresca evita stale
  // closures. Mismo patrón para `onSave` para defensa idéntica.
  const onCursorChangeRef = useRef(onCursorChange);
  const onSaveRef = useRef(onSave);
  useEffect(() => {
    onCursorChangeRef.current = onCursorChange;
  }, [onCursorChange]);
  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);
  // P-405: capturamos los disposables de Monaco para liberarlos en unmount.
  const disposablesRef = useRef<Array<{ dispose: () => void }>>([]);

  useImperativeHandle(
    ref,
    () => ({
      revealLine(line: number): void {
        const monaco = monacoRef.current;
        const editor = editorRef.current;
        if (!monaco || !editor) return;
        const safeLine = Math.max(1, Math.floor(line));
        editor.revealLineInCenterIfOutsideViewport(safeLine);
        editor.setPosition({ lineNumber: safeLine, column: 1 });
        editor.focus();

        // P-Story 11.3: highlight transient via decoration collection.
        // Limpiamos cualquier highlight previo antes de aplicar el nuevo.
        if (decorationsRef.current) decorationsRef.current.clear();
        if (decorationTimerRef.current != null) window.clearTimeout(decorationTimerRef.current);

        const collection = editor.createDecorationsCollection([
          {
            range: new monaco.Range(safeLine, 1, safeLine, 1),
            options: {
              isWholeLine: true,
              className: 'revel-step-highlight',
              linesDecorationsClassName: 'revel-step-highlight-margin',
            },
          },
        ]);
        decorationsRef.current = collection;
        decorationTimerRef.current = window.setTimeout(() => {
          collection.clear();
          decorationsRef.current = null;
          decorationTimerRef.current = null;
        }, HIGHLIGHT_DURATION_MS);
      },
    }),
    [],
  );

  const handleMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // P-330/331/332: setup global de Monaco — workers locales + monaco-yaml
    // con el JSON Schema del DSL. Idempotente: si ya corrió no-op.
    void setupMonaco(monaco);

    // Custom completion provider: variables interpolables `{reply.body}` /
    // `{session.x}` / `{flow.x}` cuando el cursor está dentro de `{...}`,
    // y snippets de step keys (`send_message`, `wait_for`, etc.) en step
    // root. Registrado una sola vez por proceso (idempotente via flag).
    if (!completionProviderRegistered) {
      registerCompletionProvider(monaco);
      completionProviderRegistered = true;
    }

    // Tema oscuro custom — colores ligados a CSS variables del shell.
    monaco.editor.defineTheme(THEME_NAME, {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'string', foreground: '7DD3FC' },
        { token: 'number', foreground: 'F0ABFC' },
        { token: 'comment', foreground: '6B7280', fontStyle: 'italic' },
      ],
      colors: {
        'editor.background': '#0F172A',
        'editor.foreground': '#E5E7EB',
        'editorLineNumber.foreground': '#475569',
        'editor.lineHighlightBackground': '#1E293B',
        'editor.selectionBackground': '#334155',
      },
    });
    monaco.editor.setTheme(THEME_NAME);

    // Cmd+S / Ctrl+S → onSave (sin que el browser muestre Save Page).
    // P-404: leer del ref para no quedar con `onSave` stale tras re-render.
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      onSaveRef.current?.();
    });

    // Story 11.3 — cursor change → callback (debounced upstream).
    // P-404/P-405: leer del ref para no capturar la primera `onCursorChange`;
    // capturar el disposable para limpiar en unmount.
    const cursorSub = editor.onDidChangeCursorPosition((e) => {
      onCursorChangeRef.current?.(e.position.lineNumber);
    });
    disposablesRef.current.push(cursorSub);

    // P-407: notificar que el editor está listo — el caller puede flushear
    // acciones imperativas pendientes (e.g. `revealLine` queued).
    onReady?.();
  };

  // Re-aplica markers cuando `problems` cambia.
  useEffect(() => {
    const monaco = monacoRef.current;
    const editor = editorRef.current;
    if (!monaco || !editor) return;
    const model = editor.getModel();
    if (!model) return;
    const fileFilter = (p: ValidationProblem) =>
      p.location?.filePath === path || !p.location?.filePath;
    const buildMarker = (severity: number) => (p: ValidationProblem) => ({
      severity,
      message: p.message + (p.suggestion ? `\n💡 ${p.suggestion}` : ''),
      startLineNumber: p.location?.line ?? 1,
      startColumn: p.location?.column ?? 1,
      endLineNumber: p.location?.line ?? 1,
      // Subrayar la palabra completa, no un solo carácter — Monaco
      // calcula end-of-word cuando endColumn excede la línea, así que
      // usamos un valor grande y dejamos que Monaco lo trunque.
      endColumn: 1024,
      source: 'revel-dsl',
    });
    const errorMarkers = (problems ?? [])
      .filter(fileFilter)
      .map(buildMarker(monaco.MarkerSeverity.Error));
    const warningMarkers = (warnings ?? [])
      .filter(fileFilter)
      .map(buildMarker(monaco.MarkerSeverity.Warning));
    monaco.editor.setModelMarkers(model, 'revel-dsl', [...errorMarkers, ...warningMarkers]);
  }, [problems, warnings, path]);

  // Cleanup del timer del highlight + disposables de Monaco al unmount.
  // P-405: liberar el `onDidChangeCursorPosition` listener; sin esto,
  // remounts del editor (key={path} en EditorPage) podían stack listeners.
  useEffect(() => {
    return () => {
      if (decorationTimerRef.current != null) window.clearTimeout(decorationTimerRef.current);
      if (decorationsRef.current) decorationsRef.current.clear();
      for (const d of disposablesRef.current) {
        try {
          d.dispose();
        } catch {
          // ignore — disposable ya muerto
        }
      }
      disposablesRef.current = [];
    };
  }, []);

  // Story 11.5 — escucha el custom event `revel:insert-snippet` que dispara
  // el CommandPalette al elegir un template. Inserta el snippet en la
  // posición del cursor usando la API `insertSnippet` de Monaco (maneja
  // tab stops `${1:...}` automáticamente).
  // P-467 (review 11.5): si el evento incluye `detail.path`, solo inserta
  // cuando matchea con este editor — previene que dos `<CodeEditor>`
  // simultáneos (split view futuro) inserten en ambos. Si no incluye
  // path, mantenemos comportamiento legacy (back-compat).
  useEffect(() => {
    function onInsert(e: Event) {
      const detail = (e as CustomEvent<{ body?: string; path?: string }>).detail;
      if (detail?.path != null && detail.path !== path) return;
      const editor = editorRef.current;
      if (!editor || !detail?.body) return;
      editor.focus();
      // `editor.action.insertSnippet` interpreta los placeholders Monaco-style.
      editor.trigger('command-palette', 'editor.action.insertSnippet', {
        snippet: detail.body,
      });
    }
    window.addEventListener('revel:insert-snippet', onInsert);
    return () => window.removeEventListener('revel:insert-snippet', onInsert);
  }, [path]);

  return (
    <div className="h-full w-full" data-monaco-path={path}>
      <Editor
        path={path}
        language={language}
        value={value}
        onChange={(next) => onChange?.(next ?? '')}
        onMount={handleMount}
        theme={THEME_NAME}
        loading={<div className="p-3 text-sm text-muted-foreground">Cargando editor…</div>}
        options={{
          readOnly,
          minimap: { enabled: false },
          wordWrap: 'on',
          automaticLayout: true,
          fontSize: 13,
          tabSize: 2,
          insertSpaces: true,
          renderWhitespace: 'selection',
          scrollBeyondLastLine: false,
          // Chromium 121+ activa autonomamente la EditContext API sobre el
          // textarea de Monaco — el target del keydown pasa a un DIV
          // `native-edit-context` y la tecla espacio se pierde antes de que
          // el modelo se actualice. Forzar `editContext: false` mantiene a
          // Monaco con su textarea tradicional, donde el input funciona
          // correcto en todos los browsers.
          editContext: false,
          // Sugerencias automáticas mientras tipea (no requiere Ctrl+Space).
          // El custom completion provider expone `{reply.body}`, snippets
          // de step keys, etc.
          quickSuggestions: { other: true, comments: false, strings: true },
          // No aceptar suggestion al teclear espacio/`,`/etc. (bug fix).
          // Tab y Enter siguen aceptando.
          acceptSuggestionOnCommitCharacter: false,
          suggestOnTriggerCharacters: true,
        }}
      />
    </div>
  );
});
